import { defineStore } from 'pinia';

import { useStateStore } from '@/stores/state.store';
import {
  getImage,
  IImage,
  ImageClass,
  initializeImages,
  resetImages,
} from '../common';
import Tr from '@/i18n/translation';
import { DataService } from '@/services/index.js';
import RsvpService from '../../services/RsvpService.js';
import ImageService from '../../services/ImageService.js';
import { Logger } from '../../middlewares/sentry';
import {
  IRsvpAdditionalQuestionsLang,
  IRsvpPlacesLang,
  IRsvpWishesLang,
  RsvpLangClass,
} from 'interface/data/rsvp/lang';
import { IGuestData, IRsvpImagesStructure, IRsvpState } from './rsvp.state.js';
import { ImageType } from 'interface/data/image/type';
import {
  IRsvpAdditionalQuestionsCommon,
  IRsvpPlacesCommon,
  RsvpCommonClass,
} from 'interface/data/rsvp/common';
import { RsvpDataClass } from 'interface/data/rsvp/data';

class RsvpImagesStructureClass implements IRsvpImagesStructure {
  [ImageType.RSVPMAIN]: IImage = new ImageClass();
  [ImageType.RSVPBACKGROUND]: IImage = new ImageClass();
  [ImageType.RSVPWISHES]: Array<IImage> = [];
  [ImageType.RSVPFORM]: IImage = new ImageClass();
}

export const useRsvpStore = defineStore('rsvp', {
  state: (): IRsvpState => ({
    clientImages: initializeImages(RsvpImagesStructureClass),
    clientData: new RsvpDataClass(),
    guestData: [],
    isDataReceived: false,
    isLangDataReceived: false,
  }),
  actions: {
    async trySetupData() {
      await this._setRsvpCommonData();
      const lang = Tr.currentLocale;
      this.tryGetLangDataWithImages(lang);

      const { getDefaultLang } = useStateStore();
      this.tryGetLangDataWithImages(getDefaultLang);
    },
    async _setRsvpCommonData() {
      if (!this.isDataReceived) {
        await this._tryGetRsvpCommonData();
        this.isDataReceived = true;
      } else {
        new Promise(async () => {
          this._tryGetRsvpCommonData();
        });
      }

      const { setColors } = useStateStore();
      setColors(
        this.clientData.common.mainColor,
        this.clientData.common.buttonColor,
        this.clientData.common.backgroundColor,
      );
    },
    async _tryGetRsvpCommonData() {
      try {
        const data = await DataService.getRsvpDataV2();
        this.clientData.common = new RsvpCommonClass(data);
      } catch (err) {
        Logger.error(err);
        if (!this.isDataReceived) {
          this.clientData.common =
            await this._fallbackToOldInterface(RsvpLangClass);
        }
      }
    },
    async tryGetLangDataWithImages(lang: string) {
      await this._setGetLangData(lang);
      this._tryGetClientImages(lang);
    },
    async _setGetLangData(lang) {
      if (!this.isLangDataReceived) {
        await this.tryGetLangData(lang);
        this.isLangDataReceived = true;
      } else {
        new Promise(async () => {
          this.tryGetLangData(lang);
        });
      }
    },
    async tryGetLangData(lang: string) {
      try {
        const data = await DataService.getRsvpDataV2(lang);
        this.clientData.lang[lang] = new RsvpLangClass(data);
      } catch (err) {
        Logger.error(err);
        if (!this.isLangDataReceived) {
          const { getDefaultLang } = useStateStore();

          if (lang !== getDefaultLang) {
            this.tryGetLangData(getDefaultLang);
            return;
          }

          this.clientData.lang[lang] =
            await this._fallbackToOldInterface(RsvpLangClass);
        }
      }
    },
    async _fallbackToOldInterface(Interface) {
      try {
        const data = await DataService.getRsvpData();
        return new Interface(data);
      } catch (err) {
        Logger.error(err);
        return new Interface({});
      }
    },
    async _tryGetClientImages(lang: string) {
      try {
        if (!this.clientImages[lang]) {
          this.clientImages[lang] = new RsvpImagesStructureClass();
        }

        const data = await ImageService.getImagesInfo();
        getImage(this.clientImages, data, ImageType.RSVPMAIN, lang);
        getImage(this.clientImages, data, ImageType.RSVPBACKGROUND, lang);
        getImage(this.clientImages, data, ImageType.RSVPFORM, lang);
        if (this.getWishes().lang) {
          for (let i = 0; i < this.getWishes().lang.length; ++i) {
            getImage(this.clientImages, data, ImageType.RSVPWISHES, lang, i);
          }
        }
      } catch (err) {
        Logger.error(err);
      }
    },
    resetGuestData() {
      this.guestData = [];
    },
    addGuest() {
      const dataPattern: IGuestData = {
        name: '',
        surname: '',
        presence: true,
        additionalQuestions: [],
      };

      this.getAdditionalQuestions().lang.forEach(
        (q: IRsvpAdditionalQuestionsLang) => {
          dataPattern.additionalQuestions.push(q.default);
        },
      );

      this.guestData.push(dataPattern);
    },
    removeGuest(id: number) {
      this.guestData.splice(id, 1);
    },
    validate() {
      for (const i in this.guestData) {
        if (
          this.guestData[i]?.name === '' ||
          this.guestData[i]?.surname === ''
        ) {
          return false;
        }
      }

      return true;
    },
    async send() {
      try {
        for (let i = 0; i < this.guestData.length; ++i) {
          const gD = this.guestData[i];
          const questions = {};
          const { getDefaultLang } = useStateStore();

          // TODO Brany jest default z state store - potrzebne zmiany w zapisywaniu nazw pól i wyświetlaniu w panelu admina
          for (
            let j = 0;
            j <
            (this.clientData.lang[getDefaultLang]?.additionalQuestions.length ??
              0);
            ++j
          ) {
            const question: string =
              this.clientData.lang[getDefaultLang]?.additionalQuestions[
                j
              ]?.header.trim() !== ''
                ? (this.clientData.lang[getDefaultLang]?.additionalQuestions[j]
                    ?.header ?? '')
                : (this.clientData.lang[getDefaultLang]?.additionalQuestions[j]
                    ?.querry ?? '');

            questions[question] = gD?.additionalQuestions[j] ?? '';
          }

          const elem = {
            name: gD?.name,
            surname: gD?.surname,
            presence: gD?.presence,
            ...questions,
          };

          await RsvpService.postRsvp(elem);
        }

        this.resetGuestData();
        return true;
      } catch (err) {
        Logger.error(err);
      }

      return false;
    },
    getAdditionalQuestionsById(
      id: number,
      lang = Tr.currentLocale,
    ): {
      common: IRsvpAdditionalQuestionsCommon | undefined;
      lang: IRsvpAdditionalQuestionsLang | undefined;
    } {
      const commonAdditionalQuestions =
        this.clientData.common?.additionalQuestions || [];
      const langAdditionalQuestions =
        this.getLangData<IRsvpAdditionalQuestionsLang[]>(
          'additionalQuestions',
          lang,
        ) || [];

      if (id < 0) {
        Logger.warn(`Invalid negative index: ${id}`);
      }

      if (id >= commonAdditionalQuestions.length) {
        Logger.warn(
          `Index out of range for commonAdditionalQuestions: ${id}. commonAdditionalQuestions size: ${commonAdditionalQuestions.length}`,
        );
      }

      if (id >= langAdditionalQuestions.length) {
        Logger.warn(
          `Index out of range for langAdditionalQuestions: ${id}. langAdditionalQuestions size: ${langAdditionalQuestions.length}`,
        );
      }

      return {
        common: commonAdditionalQuestions[id],
        lang: langAdditionalQuestions[id],
      };
    },
    getPlacesById(
      id: number,
      lang = Tr.currentLocale,
    ): {
      common: IRsvpPlacesCommon | undefined;
      lang: IRsvpPlacesLang | undefined;
    } {
      const commonPlaces: IRsvpPlacesCommon[] =
        this.clientData.common.places || [];
      const langPlaces: IRsvpPlacesLang[] =
        this.getLangData<IRsvpPlacesLang[]>('places', lang) || [];

      if (id < 0) {
        Logger.warn(`Invalid negative index: ${id}`);
      }

      if (id >= commonPlaces.length) {
        Logger.warn(
          `Index out of range for commonPlaces: ${id}. commonPlaces size: ${commonPlaces.length}`,
        );
      }

      if (id >= langPlaces.length) {
        Logger.warn(
          `Index out of range for langPlaces: ${id}. langPlaces size: ${langPlaces.length}`,
        );
      }

      return { common: commonPlaces[id], lang: langPlaces[id] };
    },
    getWishById(
      id: number,
      lang = Tr.currentLocale,
    ): {
      lang: IRsvpWishesLang | undefined;
    } {
      const langWishes =
        this.getLangData<IRsvpWishesLang[]>('wishes', lang) || [];

      if (id < 0 || id >= langWishes.length) {
        Logger.warn(
          `Index out of range: ${id}. Lang wishes size: ${langWishes.length}`,
        );
      }

      return {
        lang: langWishes[id],
      };
    },
    getAdditionalQuestions(lang = Tr.currentLocale): {
      common: IRsvpAdditionalQuestionsCommon[];
      lang: IRsvpAdditionalQuestionsLang[];
    } {
      return {
        common: this.clientData.common.additionalQuestions ?? [],
        lang: this.getLangData('additionalQuestions', lang) ?? [],
      };
    },
    getPlaces(lang = Tr.currentLocale): {
      common: IRsvpPlacesCommon[];
      lang: IRsvpPlacesLang[];
    } {
      return {
        common: this.clientData.common.places ?? [],
        lang: this.getLangData('places', lang) ?? [],
      };
    },
    getWishes(lang = Tr.currentLocale): {
      lang: IRsvpWishesLang[];
    } {
      return { lang: this.getLangData('wishes', lang) ?? [] };
    },
    getLangData<T = unknown>(data: string, lang = Tr.currentLocale) {
      if (!this.clientData.lang[lang]) {
        this.clientData.lang[lang] = new RsvpLangClass({});
      }
      return this.clientData.lang[lang].getValue<T>(data);
    },
    getLangAllLangData(lang = Tr.currentLocale) {
      if (!this.clientData.lang[lang]) {
        this.clientData.lang[lang] = new RsvpLangClass({});
      }
      return this.clientData.lang[lang];
    },
    resetLangData(lang: string) {
      this.clientData.lang[lang] = new RsvpLangClass({});
    },
    resetData() {
      this.resetGuestData();
      this.clientData = new RsvpDataClass();
      resetImages(this.clientImages);
      this.isDataReceived = false;
      this.isLangDataReceived = false;
    },

    isAnyAdditionalQuestions() {
      return (
        this.isAnyAdditionalCommonQuestions && this.isAnyAdditionalLangQuestions
      );
    },
    isAnyPlace() {
      return this.isAnyPlaceCommon && this.isAnyPlaceLang;
    },
  },
  getters: {
    getImages: (state) => {
      const lang = Tr.currentLocale;
      if (!state.clientImages[lang]) {
        state.clientImages[lang] = new RsvpImagesStructureClass();
      }

      return state.clientImages[lang];
    },
    getCommonData: (state) => state.clientData.common,
    isAnyAdditionalCommonQuestions: (state) =>
      state.clientData &&
      state.clientData.common &&
      state.clientData.common.additionalQuestions &&
      state.clientData.common.additionalQuestions.length !== 0,
    isAnyAdditionalLangQuestions: (state) =>
      state.clientData.lang[Tr.currentLocale] &&
      state.clientData.lang[Tr.currentLocale]?.additionalQuestions &&
      state.clientData.lang[Tr.currentLocale]?.additionalQuestions.length !== 0,
    isAnyPlaceCommon: (state) =>
      state.clientData &&
      state.clientData.common &&
      state.clientData.common.places &&
      state.clientData.common.places.length !== 0,
    isAnyPlaceLang: (state) =>
      state.clientData &&
      state.clientData.lang[Tr.currentLocale] &&
      state.clientData.lang[Tr.currentLocale]?.places &&
      state.clientData.lang[Tr.currentLocale]?.places.length !== 0,
    isAnyWish: (state) =>
      state.clientData &&
      state.clientData.lang[Tr.currentLocale] &&
      state.clientData.lang[Tr.currentLocale]?.wishes &&
      state.clientData.lang[Tr.currentLocale]?.wishes.length !== 0,
    getNumberOfGuests: (state) => state.guestData.length,
    getColors: (state) => {
      return {
        mainColor: state.clientData.common.mainColor,
        backgroundColor: state.clientData.common.backgroundColor,
      };
    },
  },
});
